import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';

type AuthMode = 'signIn' | 'signUp';

export function SignInPage() {
  const { signIn, register, sendPasswordReset } = useAuth();
  const [mode, setMode] = useState<AuthMode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const primaryLabel = useMemo(() => (mode === 'signIn' ? 'Sign In' : 'Create Account'), [mode]);
  const secondaryPrompt = useMemo(
    () => (mode === 'signIn' ? "Don't have an account?" : 'Already registered?'),
    [mode],
  );
  const secondaryLinkLabel = useMemo(() => (mode === 'signIn' ? 'Sign up' : 'Sign in'), [mode]);

  const toggleMode = () => {
    setMode(prev => (prev === 'signIn' ? 'signUp' : 'signIn'));
    setError(null);
    setInfo(null);
    setPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setInfo(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError('Enter both email and password.');
      return;
    }

    if (mode === 'signUp') {
      if (password.length < 6) {
        setError('Password must be at least 6 characters long.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    try {
      setLoading(true);
      if (mode === 'signIn') {
        await signIn(trimmedEmail, password);
      } else {
        await register(trimmedEmail, password);
        setInfo('Account created successfully! You can start calculating.');
      }
    } catch (authError) {
      console.error('Authentication error', authError);
      setError('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setError(null);
    setInfo(null);

    if (!email.trim()) {
      setError('Enter your email to receive a reset link.');
      return;
    }

    try {
      setLoading(true);
      await sendPasswordReset(email.trim());
      setInfo('Reset instructions sent if the account exists.');
    } catch (resetError) {
      console.error('Password reset error', resetError);
      setError('Unable to send reset email right now.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={handleSubmit}>
        <div className="auth-header">
          <div className="auth-brand">
            <img className="auth-brand-logo" src="/deathestimate-logo.png" alt="Deathestimate" />
            <div className="auth-brand-text">
              <h1 className="auth-title">{mode === 'signIn' ? 'Deathestimate' : 'Create an account'}</h1>
              <p className="auth-tagline">Access the mortality calculator web beta.</p>
            </div>
          </div>
        </div>

        {error ? <p className="auth-error">{error}</p> : null}
        {info ? <p className="auth-info">{info}</p> : null}

        <label className="input-group">
          <span>Email</span>
          <input
            type="email"
            autoComplete="email"
            placeholder="name@example.com"
            value={email}
            onChange={event => setEmail(event.target.value)}
          />
        </label>

        <label className="input-group">
          <span>Password</span>
          <input
            type="password"
            autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
            placeholder="••••••••"
            value={password}
            onChange={event => setPassword(event.target.value)}
          />
        </label>

        {mode === 'signUp' ? (
          <label className="input-group">
            <span>Confirm password</span>
            <input
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={event => setConfirmPassword(event.target.value)}
            />
          </label>
        ) : null}

        {mode === 'signIn' ? (
          <button type="button" className="link-button" onClick={handleResetPassword} disabled={loading}>
            Forgot password?
          </button>
        ) : null}

        <button className="button-primary" type="submit" disabled={loading}>
          {loading ? 'Please wait…' : primaryLabel}
        </button>

        <p className="auth-switch">
          {secondaryPrompt}{' '}
          <button type="button" className="link-button" onClick={toggleMode}>
            {secondaryLinkLabel}
          </button>
        </p>

        <p className="muted" style={{ textAlign: 'center', fontSize: '0.85rem' }}>
          By continuing you agree to our <Link to="/terms">Terms of Use</Link>.
        </p>
      </form>
    </div>
  );
}
